import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { anyGrantSatisfies } from '../utils/permissionMatch';

// Configure axios to include credentials in requests
axios.defaults.withCredentials = true;

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Update user data
  const updateUser = (userData) => {
    if (!user) return;
    setUser({ ...user, ...userData });
  };

  // Function to fetch widget preferences and store them in localStorage
  const syncWidgetPreferences = async () => {
    try {
      const res = await axios.get('/api/users/me/widget-preferences');
      
      // Check if floating shortcut widget preferences exist
      if (res.data && res.data.floatingShortcut) {
        const { favoriteShortcuts, enabled } = res.data.floatingShortcut;
        
        // Store in localStorage
        if (favoriteShortcuts) {
          localStorage.setItem('favoriteShortcuts', JSON.stringify(favoriteShortcuts));
        }
        
        if (enabled !== undefined) {
          localStorage.setItem('floatingShortcutWidgetEnabled', enabled ? 'true' : 'false');
        }
        
        console.log('Widget preferences synced from database to localStorage');
      }
    } catch (err) {
      console.error('Error syncing widget preferences:', err);
      // Continue silently - this is not critical functionality
    }
  };

  // Load user on initial render
  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await axios.get('/api/auth/current');
        
        // If user has roles, fetch role details with permissions
        if (res.data && res.data.roles && res.data.roles.length > 0) {
          try {
            const rolesRes = await axios.get('/api/roles/my-permissions');
            res.data.roleDetails = rolesRes.data;
          } catch (roleErr) {
            console.error('Error loading role details:', roleErr);
            // Continue without role details
          }
        }
        
        setUser(res.data);
        
        // After user is loaded, sync widget preferences
        await syncWidgetPreferences();
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading user:', err);
        setUser(null);
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Login with Google
  const loginWithGoogle = () => {
    window.location.href = '/api/auth/google';
  };

  // Login with email and password
  const loginWithCredentials = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('/api/auth/login', { email, password });

      // Check if email 2FA verification is required (off-network login)
      if (res.data.requiresEmail2FA) {
        return {
          requiresEmail2FA: true,
          userId: res.data.userId,
          msg: res.data.msg
        };
      }

      // Check if TOTP 2FA verification is required
      if (res.data.requires2FA) {
        return {
          requires2FA: true,
          userId: res.data.userId,
          msg: res.data.msg
        };
      }

      // Check if 2FA setup is required
      if (res.data.requiresSetup2FA) {
        return {
          requiresSetup2FA: true,
          userId: res.data.userId,
          msg: res.data.msg
        };
      }

      // Regular login success
      setUser(res.data);

      // Sync widget preferences after login
      await syncWidgetPreferences();

      return { success: true };
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.msg || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    try {
      await axios.get('/api/auth/logout');
      setUser(null);
    } catch (err) {
      console.error('Error logging out:', err);
      setError('Error logging out');
    }
  };

  // Check if user has a specific role
  const hasRole = (role) => {
    if (!user) return false;
    return user.roles.includes(role);
  };

  // Check if user has admin role
  const isAdmin = () => {
    return hasRole('admin');
  };

  // Check if user is using Google authentication
  const isGoogleUser = () => {
    return user && user.authType === 'google';
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user || !user.roles || !user.roles.length) return false;
    
    // Admin role has all permissions
    if (user.roles.includes('admin')) return true;
    
    // If user has roles but we don't have role details with permissions
    if (!user.roleDetails) return false;
    
    // Check if any of the user's roles grant the required permission.
    // Uses the same hierarchical matching as the backend (permissionMatch.js),
    // so a scoped grant like "amazon:read:own" satisfies an "amazon:read" gate,
    // and higher levels (write/delete/admin/...) satisfy lower ones.
    return user.roleDetails.some(role => anyGrantSatisfies(role.permissions, permission));
  };

  // All integrations are now assumed to be active for all users
  // Access is controlled by roles and permissions instead
  
  // This function now always returns true since all integrations are active
  const hasIntegration = () => {
    return true;
  };

  // Value object to be provided to consumers
  const value = {
    user,
    loading,
    error,
    loginWithGoogle,
    loginWithCredentials,
    logout,
    hasRole,
    isAdmin,
    isGoogleUser,
    hasPermission,
    hasIntegration,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
